// apps/frontend/src/components/common/FounderLiveBanner.tsx
// Bandeau global "Marie-Amelie en live" — annonce un live externe
// (Insta / YouTube / TikTok) ou signale qu'il est EN DIRECT MAINTENANT.
// Donnees reelles uniquement (hook useUpcomingFounderLive). Si pas de
// live a venir / en cours dans les 7 jours, le bandeau ne s'affiche pas.

import React, { useState } from 'react';
import { Radio, Calendar, ExternalLink, X } from 'lucide-react';
import { useUpcomingFounderLive } from '../../hooks/useUpcomingFounderLive';

const DISMISS_KEY_PREFIX = 'glowp_founder_live_dismissed_';

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = d.getTime() - now;
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    if (diffHours <= 0) return 'maintenant';
    if (diffHours < 1) {
      const min = Math.max(1, Math.round(diffMs / 60_000));
      return `dans ${min} min`;
    }
    if (diffHours < 24) return `dans ${diffHours}h`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays === 1) return 'demain';
    if (diffDays < 7) {
      return d.toLocaleDateString('fr-FR', { weekday: 'long' });
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

function formatTimeOfDay(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const FounderLiveBanner: React.FC = () => {
  const live = useUpcomingFounderLive();
  const [dismissedId, setDismissedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('glowp_founder_live_last_dismissed');
    } catch {
      return null;
    }
  });

  if (!live) return null;
  if (live.status === 'ENDED' || live.status === 'CANCELLED') return null;

  const isLiveNow = live.status === 'LIVE';

  // Dismiss UX : un live SCHEDULED peut etre cache temporairement. Quand
  // il passe LIVE, on reaffiche meme si dismiss precedemment (vrai event).
  if (!isLiveNow && dismissedId === live.id) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem('glowp_founder_live_last_dismissed', live.id);
      localStorage.setItem(
        DISMISS_KEY_PREFIX + live.id,
        new Date().toISOString()
      );
    } catch {
      /* localStorage indisponible */
    }
    setDismissedId(live.id);
  };

  const handleOpen = () => {
    window.open(live.externalUrl, '_blank', 'noopener,noreferrer');
  };

  if (isLiveNow) {
    return (
      <div className="w-full bg-gradient-to-r from-rose-600 to-rose-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
          {/* Pastille LIVE pulsante */}
          <span className="relative flex items-center justify-center flex-shrink-0">
            <span className="absolute inline-flex h-3 w-3 rounded-full bg-white/60 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          <Radio className="w-4 h-4 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight truncate">
              EN DIRECT — {live.title}
            </p>
            {live.description && (
              <p className="text-[11px] opacity-90 truncate">
                {live.description}
              </p>
            )}
          </div>
          <button
            onClick={handleOpen}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-rose-700 text-xs font-semibold hover:bg-rose-50 transition-colors"
          >
            Rejoindre
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  // SCHEDULED — bandeau brand chaleureux
  return (
    <div className="w-full bg-gradient-to-r from-[#FBF5EE] via-[#F5EBD9] to-[#FBF5EE] border-b border-brand-border">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border border-brand-border">
          <Calendar className="w-4 h-4 text-brand-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-dark leading-tight truncate">
            {live.title} · {formatRelative(live.scheduledAt)} à{' '}
            {formatTimeOfDay(live.scheduledAt)}
          </p>
          {live.description && (
            <p className="text-[11px] text-brand-warm truncate">
              {live.description}
            </p>
          )}
        </div>
        <button
          onClick={handleOpen}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#C8A882] to-[#B89968] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
        >
          Voir
          <ExternalLink className="w-3 h-3" />
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Masquer"
          className="flex-shrink-0 w-7 h-7 rounded-full hover:bg-white/60 flex items-center justify-center text-brand-warm transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default FounderLiveBanner;
